import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, MoreVertical, Edit, Trash2, Eye, FileText, Filter, Upload, Download, Loader2, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { formatCurrency, formatDate, getEstadoInfo, truncate } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADOS = [
    { value: 'servicio_en_curso', label: 'En Curso' },
    { value: 'servicio_terminado', label: 'Terminado' },
    { value: 'edp_enviado', label: 'EDP Enviado' },
    { value: 'espera_oc', label: 'Espera OC' },
    { value: 'edp_aprobado', label: 'EDP Aprobado' },
    { value: 'edp_rechazado', label: 'EDP Rechazado' },
    { value: 'facturado', label: 'Facturado' },
    { value: 'pendiente_pago', label: 'Pendiente Pago' },
    { value: 'pago_parcial', label: 'Pago Parcial' },
    { value: 'pagado', label: 'Pagado' },
    { value: 'vencido', label: 'Vencido' },
    { value: 'en_mora', label: 'En Mora' },
    { value: 'cobranza_activa', label: 'Cobranza Activa' },
    { value: 'compromiso_pago', label: 'Compromiso Pago' },
    { value: 'servicio_interrumpido', label: 'Interrumpido' },
    { value: 'castigado', label: 'Castigado' }
];

const Servicios = ({ soloDelMes = false }) => {
    const { canEdit, canDelete } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const clienteIdFromUrl = searchParams.get('cliente_id');
    
    const [loading, setLoading] = useState(true);
    const [servicios, setServicios] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [rutas, setRutas] = useState([]);
    const [filterCliente, setFilterCliente] = useState(clienteIdFromUrl || '');
    const [filterEstado, setFilterEstado] = useState('');
    const [filterVencidos, setFilterVencidos] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    // Estados para paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const pageSize = 10;
    
    // Estados para selección múltiple y eliminación
    const [selectedIds, setSelectedIds] = useState([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [servicioToDelete, setServicioToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // Filtro de período
    // En "Servicios del Mes", default = mes/año actual (formato "MM-YYYY")
    const periodoActualDefault = `${new Date().getMonth() + 1}-${new Date().getFullYear()}`;
    const [filterPeriodo, setFilterPeriodo] = useState(soloDelMes ? periodoActualDefault : ''); // formato: "MM-YYYY"

    // Reset filterPeriodo cuando cambia el modo (al navegar entre /servicios y /servicios/mes
    // React reutiliza el mismo componente y conserva el estado). Por eso forzamos sync.
    useEffect(() => {
        setFilterPeriodo(soloDelMes ? periodoActualDefault : '');
        setFilterEstado('');
        setFilterVencidos(false);
        setSearchTerm('');
        setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [soloDelMes]);
    
    // Búsqueda en tiempo real
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Para botón "Ver EDP"
    const [loadingEdpId, setLoadingEdpId] = useState(null);

    const handleVerEdp = async (servicioId) => {
        try {
            setLoadingEdpId(servicioId);
            const resp = await axios.get(`${API_URL}/api/servicios/${servicioId}/edp-pdf`);
            // Bypass browser cache for previously-viewed PDFs
            const pdfUrl = `${window.location.origin}${resp.data.pdf_url}?t=${Date.now()}`;
            window.open(pdfUrl, '_blank');
        } catch (error) {
            console.error('Error generando EDP:', error);
            toast.error(error.response?.data?.detail || 'Error al generar el EDP');
        } finally {
            setLoadingEdpId(null);
        }
    };
    
    // Debounce para la búsqueda
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setCurrentPage(1); // Reset to page 1 when searching
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);
    
    const [formData, setFormData] = useState({
        cliente_id: '',
        nombre_producto: '',
        faena: '',
        ruta_cobro_id: '',
        tipo_pago: 'unico',
        es_recurrente: false,
        periodo_mes: new Date().getMonth() + 1,
        periodo_anio: new Date().getFullYear(),
        periodo_fin_mes: new Date().getMonth() + 1,
        periodo_fin_anio: new Date().getFullYear(),
        fecha_inicio: '',
        fecha_termino: '',
        monto_neto: '',
        exento_iva: false,
        requiere_edp: false,
        condicion_pago_dias: 30,
        observaciones: ''
    });

    // Cálculo automático de IVA y Total
    const montoNeto = parseFloat(formData.monto_neto) || 0;
    const iva = formData.exento_iva ? 0 : montoNeto * 0.19;
    const montoTotal = montoNeto + iva;
    
    // Calcular cantidad de meses si es recurrente
    const calcularMesesRecurrentes = () => {
        if (!formData.es_recurrente) return 1;
        const inicio = new Date(formData.periodo_anio, formData.periodo_mes - 1);
        const fin = new Date(formData.periodo_fin_anio, formData.periodo_fin_mes - 1);
        const diffMonths = (fin.getFullYear() - inicio.getFullYear()) * 12 + (fin.getMonth() - inicio.getMonth()) + 1;
        return Math.max(1, diffMonths);
    };
    const mesesRecurrentes = calcularMesesRecurrentes();

    useEffect(() => {
        fetchData();
    }, [filterEstado, filterVencidos, filterPeriodo, filterCliente, currentPage, debouncedSearch]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filterEstado && filterEstado !== 'all') params.append('estado', filterEstado);
            if (filterVencidos) params.append('vencidos', 'true');
            if (filterCliente) params.append('cliente_id', filterCliente);
            if (filterPeriodo && filterPeriodo !== 'all') {
                const [mes, anio] = filterPeriodo.split('-');
                params.append('periodo_mes', mes);
                params.append('periodo_anio', anio);
            }
            if (debouncedSearch) params.append('buscar', debouncedSearch);
            params.append('page', currentPage);
            params.append('page_size', pageSize);
            
            const [serviciosRes, clientesRes, rutasRes] = await Promise.all([
                axios.get(`${API_URL}/api/servicios?${params}`),
                axios.get(`${API_URL}/api/clientes`),
                axios.get(`${API_URL}/api/rutas-cobro`).catch(() => ({ data: [] })),
            ]);
            
            setServicios(serviciosRes.data.items);
            setTotalPages(serviciosRes.data.total_pages);
            setTotalItems(serviciosRes.data.total);
            setClientes(clientesRes.data);
            setRutas(rutasRes.data || []);
            setSelectedIds([]); // Reset selection on page change
        } catch (error) {
            console.error('Error fetching servicios:', error);
            toast.error('Error al cargar servicios');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setFormData({
            cliente_id: '',
            nombre_producto: '',
            faena: '',
            ruta_cobro_id: '',
            tipo_pago: 'unico',
            es_recurrente: false,
            periodo_mes: new Date().getMonth() + 1,
            periodo_anio: new Date().getFullYear(),
            periodo_fin_mes: new Date().getMonth() + 1,
            periodo_fin_anio: new Date().getFullYear(),
            fecha_inicio: new Date().toISOString().split('T')[0],
            fecha_termino: '',
            monto_neto: '',
            exento_iva: false,
            requiere_edp: false,
            condicion_pago_dias: 30,
            observaciones: ''
        });
        setDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                cliente_id: formData.cliente_id,
                nombre_producto: formData.nombre_producto || null,
                descripcion: formData.nombre_producto,  // Use nombre_producto as descripcion
                faena: formData.faena || null,
                ruta_cobro_id: formData.ruta_cobro_id || null,
                tipo_pago: formData.tipo_pago,
                monto_neto: parseFloat(formData.monto_neto),
                exento_iva: formData.exento_iva,
                requiere_edp: formData.requiere_edp,
                condicion_pago_dias: parseInt(formData.condicion_pago_dias),
                observaciones: formData.observaciones || null,
                fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
                fecha_termino: formData.fecha_termino ? new Date(formData.fecha_termino).toISOString() : null,
                // Campos de recurrencia
                es_recurrente: formData.es_recurrente,
                periodo_mes: parseInt(formData.periodo_mes),
                periodo_anio: parseInt(formData.periodo_anio),
                periodo_fin_mes: formData.es_recurrente ? parseInt(formData.periodo_fin_mes) : null,
                periodo_fin_anio: formData.es_recurrente ? parseInt(formData.periodo_fin_anio) : null
            };
            
            const response = await axios.post(`${API_URL}/api/servicios`, data);
            const creados = response.data.servicios_creados || 1;
            toast.success(creados > 1 ? `${creados} servicios creados` : 'Servicio creado');
            setDialogOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving servicio:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar servicio');
        }
    };

    // Funciones de selección
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedIds(servicios.map(s => s.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id, checked) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(sid => sid !== id));
        }
    };

    const isAllSelected = servicios.length > 0 && selectedIds.length === servicios.length;
    const isSomeSelected = selectedIds.length > 0 && selectedIds.length < servicios.length;

    // Abrir diálogo de confirmación para eliminar uno
    const handleDeleteClick = (servicio) => {
        setServicioToDelete(servicio);
        setDeleteDialogOpen(true);
    };

    // Abrir diálogo para eliminar seleccionados
    const handleDeleteSelectedClick = () => {
        setServicioToDelete(null); // null indica eliminación múltiple
        setDeleteDialogOpen(true);
    };

    // Confirmar eliminación
    const handleConfirmDelete = async () => {
        setDeleting(true);
        try {
            if (servicioToDelete) {
                // Eliminar uno solo
                await axios.delete(`${API_URL}/api/servicios/${servicioToDelete.id}`);
                toast.success('Servicio eliminado');
            } else {
                // Eliminar múltiples
                await Promise.all(
                    selectedIds.map(id => axios.delete(`${API_URL}/api/servicios/${id}`))
                );
                toast.success(`${selectedIds.length} servicios eliminados`);
                setSelectedIds([]);
            }
            fetchData();
        } catch (error) {
            console.error('Error deleting servicio(s):', error);
            toast.error('Error al eliminar servicio(s)');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setServicioToDelete(null);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/servicios/plantilla/excel`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'plantilla_servicios.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Plantilla descargada');
        } catch (error) {
            console.error('Error downloading template:', error);
            toast.error('Error al descargar plantilla');
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            toast.error('Por favor seleccione un archivo Excel (.xlsx o .xls)');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await axios.post(`${API_URL}/api/servicios/carga-masiva`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            const { created, skipped, errors } = response.data;
            
            if (created > 0) {
                toast.success(`${created} servicios creados exitosamente`);
            }
            if (skipped > 0) {
                toast.info(`${skipped} servicios omitidos`);
            }
            if (errors && errors.length > 0) {
                // Mostrar cada error específico
                errors.slice(0, 5).forEach((err, idx) => {
                    toast.error(err, { duration: 8000 });
                });
                if (errors.length > 5) {
                    toast.error(`... y ${errors.length - 5} errores más. Revise la consola.`, { duration: 8000 });
                }
                console.log('Todos los errores de carga:', errors);
            }
            
            if (created > 0 || errors.length === 0) {
                setUploadDialogOpen(false);
            }
            fetchData();
        } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(error.response?.data?.detail || 'Error al procesar archivo');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-2">
                    {soloDelMes && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => {
                                const [m, y] = (filterPeriodo || periodoActualDefault).split('-').map(Number);
                                const d = new Date(y, m - 2, 1);
                                setFilterPeriodo(`${d.getMonth() + 1}-${d.getFullYear()}`);
                                setCurrentPage(1);
                            }}
                            data-testid="prev-month-btn"
                            title="Mes anterior"
                        >
                            <ChevronLeft size={20} />
                        </Button>
                    )}
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            {soloDelMes ? (() => {
                                const [m, y] = (filterPeriodo || periodoActualDefault).split('-').map(Number);
                                const meses = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
                                return `Servicios de ${meses[m - 1]} ${y}`;
                            })() : 'Servicios'}
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            {soloDelMes ? '' : 'Gestión de servicios prestados y cobros'}
                            {selectedIds.length > 0 && (
                                <span className="ml-2 text-[#5B9A9A] font-medium">
                                    ({selectedIds.length} seleccionados)
                                </span>
                            )}
                        </p>
                    </div>
                    {soloDelMes && (
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9"
                            onClick={() => {
                                const [m, y] = (filterPeriodo || periodoActualDefault).split('-').map(Number);
                                const d = new Date(y, m, 1);
                                setFilterPeriodo(`${d.getMonth() + 1}-${d.getFullYear()}`);
                                setCurrentPage(1);
                            }}
                            data-testid="next-month-btn"
                            title="Mes siguiente"
                        >
                            <ChevronRight size={20} />
                        </Button>
                    )}
                </div>
                <div className="flex gap-2">
                    {selectedIds.length > 0 && canDelete() && (
                        <Button 
                            variant="destructive" 
                            onClick={handleDeleteSelectedClick}
                            data-testid="delete-selected-btn"
                        >
                            <Trash2 size={18} className="mr-2" />
                            Eliminar ({selectedIds.length})
                        </Button>
                    )}
                    {canEdit() && (
                        <>
                            <Button 
                                variant="outline" 
                                onClick={() => setUploadDialogOpen(true)}
                                data-testid="bulk-upload-servicios-btn"
                            >
                                <Upload size={18} className="mr-2" />
                                Carga Masiva
                            </Button>
                            <Button 
                                variant="outline" 
                                onClick={() => navigate('/servicios/timeline-global')}
                                data-testid="timeline-global-btn"
                            >
                                <Calendar size={18} className="mr-2" />
                                Línea de Tiempo
                            </Button>
                            <Button onClick={handleOpenDialog} data-testid="new-servicio-btn">
                                <Plus size={18} className="mr-2" />
                                Nuevo Servicio
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* KPIs del mes (solo en /servicios/mes) */}
            {soloDelMes && servicios.length > 0 && (() => {
                const total = servicios.reduce((acc, s) => acc + parseFloat(s.monto_total || 0), 0);
                const neto = servicios.reduce((acc, s) => acc + parseFloat(s.monto_neto || 0), 0);
                const iva = total - neto;
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        <Card className="border-l-4 border-l-[#5B9A9A]">
                            <CardContent className="p-4 flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">Facturación del mes</p>
                                    <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="kpi-facturacion-mes">
                                        {formatCurrency(total)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">{servicios.length} servicios · Neto {formatCurrency(neto)}</p>
                                </div>
                                <div className="w-12 h-12 rounded-full bg-[#E8F4F4] flex items-center justify-center">
                                    <FileText className="text-[#3D7A7A]" size={22} />
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="border-l-4 border-l-amber-500">
                            <CardContent className="p-4 flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-slate-500 uppercase tracking-wide font-medium">IVA del mes</p>
                                    <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="kpi-iva-mes">
                                        {formatCurrency(iva)}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">19% sobre neto facturado</p>
                                </div>
                                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center">
                                    <Calendar className="text-amber-600" size={22} />
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                );
            })()}

            {/* Search and Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap gap-4 items-center">
                        {/* Search Input */}
                        <div className="flex-1 min-w-[280px] relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                            <Input
                                type="text"
                                placeholder="Buscar por producto, cliente, factura..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="search-servicios"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        <div className="min-w-[180px]">
                            <Select value={filterEstado} onValueChange={(v) => { setFilterEstado(v); setCurrentPage(1); }}>
                                <SelectTrigger data-testid="filter-estado">
                                    <SelectValue placeholder="Filtrar por estado" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los estados</SelectItem>
                                    {ESTADOS.map((estado) => (
                                        <SelectItem key={estado.value} value={estado.value}>
                                            {estado.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="min-w-[180px]">
                            <Select value={filterPeriodo} onValueChange={(v) => { setFilterPeriodo(v); setCurrentPage(1); }}>
                                <SelectTrigger data-testid="filter-periodo">
                                    <SelectValue placeholder="Filtrar por período" />
                                </SelectTrigger>
                                <SelectContent>
                                    {!soloDelMes && <SelectItem value="all">Todos los períodos</SelectItem>}
                                    {/* Generate last 12 months plus next 3 */}
                                    {Array.from({ length: 15 }, (_, i) => {
                                        const date = new Date();
                                        date.setMonth(date.getMonth() - 11 + i);
                                        const mes = date.getMonth() + 1;
                                        const anio = date.getFullYear();
                                        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                                        return (
                                            <SelectItem key={`${mes}-${anio}`} value={`${mes}-${anio}`}>
                                                {meses[mes - 1]} {anio}
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            variant={filterVencidos ? "default" : "outline"}
                            onClick={() => {
                                setFilterVencidos(!filterVencidos);
                                setCurrentPage(1);
                            }}
                            data-testid="filter-vencidos"
                        >
                            <Filter size={16} className="mr-2" />
                            Solo vencidos
                        </Button>
                        {filterCliente && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#E8F4F4] border border-[#A5D0D0] rounded-md">
                                <span className="text-sm text-[#3D7A7A]">
                                    Cliente: {clientes.find(c => c.id === filterCliente)?.razon_social || 'Seleccionado'}
                                </span>
                                <button
                                    onClick={() => {
                                        setFilterCliente('');
                                        setSearchParams({});
                                        setCurrentPage(1);
                                    }}
                                    className="text-[#5B9A9A] hover:text-[#3D7A7A] font-bold"
                                >
                                    ×
                                </button>
                            </div>
                        )}
                        {(filterEstado || filterVencidos || filterPeriodo || searchTerm || filterCliente) && (
                            <Button
                                variant="ghost"
                                onClick={() => {
                                    setFilterEstado('');
                                    setFilterVencidos(false);
                                    setFilterPeriodo(soloDelMes ? periodoActualDefault : '');
                                    setSearchTerm('');
                                    setFilterCliente('');
                                    setSearchParams({});
                                    setCurrentPage(1);
                                }}
                            >
                                Limpiar filtros
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5B9A9A]"></div>
                        </div>
                    ) : servicios.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                            <FileText size={48} className="text-slate-300 mb-2" />
                            <p>{searchTerm ? `No se encontraron resultados para "${searchTerm}"` : 'No hay servicios registrados'}</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header border-b">
                                        <th className="text-center py-3 px-2 w-12">
                                            <Checkbox
                                                checked={isAllSelected}
                                                onCheckedChange={handleSelectAll}
                                                aria-label="Seleccionar todos"
                                                className={isSomeSelected ? "data-[state=checked]:bg-[#7AB5B5]" : ""}
                                                data-testid="select-all-checkbox"
                                            />
                                        </th>
                                        <th className="text-center py-3 px-2">Período</th>
                                        <th className="text-left py-3 px-4">Nombre Producto</th>
                                        <th className="text-left py-3 px-4">Sitio/Faena</th>
                                        <th className="text-left py-3 px-4">Cliente</th>
                                        <th className="text-right py-3 px-4">Monto</th>
                                        <th className="text-center py-3 px-4">Vencimiento</th>
                                        <th className="text-center py-3 px-4">EDP</th>
                                        <th className="text-center py-3 px-4">Estado</th>
                                        <th className="text-right py-3 px-4">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {servicios.map((servicio) => {
                                        const estadoInfo = getEstadoInfo(servicio.estado);
                                        const isSelected = selectedIds.includes(servicio.id);
                                        const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
                                        const periodoLabel = servicio.periodo_mes && servicio.periodo_anio 
                                            ? `${meses[servicio.periodo_mes - 1]} ${servicio.periodo_anio}`
                                            : '-';
                                        return (
                                            <tr 
                                                key={servicio.id} 
                                                className={`table-row cursor-pointer ${isSelected ? 'bg-[#E8F4F4]' : ''}`}
                                                data-testid={`servicio-row-${servicio.id}`}
                                            >
                                                <td className="text-center py-3 px-2" onClick={(e) => e.stopPropagation()}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={(checked) => handleSelectOne(servicio.id, checked)}
                                                        aria-label={`Seleccionar ${servicio.nombre_producto || servicio.descripcion}`}
                                                        data-testid={`checkbox-${servicio.id}`}
                                                    />
                                                </td>
                                                <td className="table-cell text-center" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    <span className="text-sm font-medium text-slate-700">{periodoLabel}</span>
                                                </td>
                                                <td className="table-cell" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    <div className="font-medium text-slate-900">
                                                        {truncate(servicio.nombre_producto || servicio.descripcion, 40)}
                                                    </div>
                                                    {servicio.numero_factura && (
                                                        <div className="text-xs text-slate-500">
                                                            Factura: {servicio.numero_factura}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="table-cell text-slate-600" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    {servicio.faena ? (
                                                        <span className="text-sm">{truncate(servicio.faena, 30)}</span>
                                                    ) : (
                                                        <span className="text-slate-300 text-sm">—</span>
                                                    )}
                                                </td>
                                                <td className="table-cell text-slate-600" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    {servicio.cliente_nombre || '-'}
                                                </td>
                                                <td className="table-cell text-right" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    <div className="font-semibold">{formatCurrency(servicio.monto_total)}</div>
                                                    {Math.round(parseFloat(servicio.saldo_pendiente) || 0) > 0 && servicio.estado !== 'pagado' && (
                                                        <div className="text-xs text-amber-600">
                                                            Pendiente: {formatCurrency(servicio.saldo_pendiente)}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="table-cell text-center" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    {formatDate(servicio.fecha_vencimiento)}
                                                </td>
                                                <td className="table-cell text-center" onClick={(e) => e.stopPropagation()}>
                                                    {servicio.requiere_edp ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 px-2 text-xs border-[#5B9A9A] text-[#3D7A7A] hover:bg-[#E8F4F4]"
                                                            disabled={loadingEdpId === servicio.id}
                                                            onClick={() => handleVerEdp(servicio.id)}
                                                            data-testid={`ver-edp-${servicio.id}`}
                                                        >
                                                            {loadingEdpId === servicio.id ? (
                                                                <Loader2 size={12} className="animate-spin" />
                                                            ) : (
                                                                <><Eye size={12} className="mr-1" /> Ver</>
                                                            )}
                                                        </Button>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs">—</span>
                                                    )}
                                                </td>
                                                <td className="table-cell text-center" onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${estadoInfo.color}`}>
                                                        {estadoInfo.label}
                                                    </span>
                                                </td>
                                                <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon">
                                                                <MoreVertical size={16} />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem onClick={() => navigate(`/servicios/${servicio.id}`)}>
                                                                <Eye size={16} className="mr-2" />
                                                                Ver detalle
                                                            </DropdownMenuItem>
                                                            {canDelete() && (
                                                                <DropdownMenuItem 
                                                                    onClick={() => handleDeleteClick(servicio)}
                                                                    className="text-red-600"
                                                                >
                                                                    <Trash2 size={16} className="mr-2" />
                                                                    Eliminar
                                                                </DropdownMenuItem>
                                                            )}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    
                    {/* Paginación */}
                    {!loading && servicios.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                            <div className="text-sm text-slate-600">
                                Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} de {totalItems} servicios
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    data-testid="prev-page-btn"
                                >
                                    <ChevronLeft size={16} className="mr-1" />
                                    Anterior
                                </Button>
                                <div className="flex items-center gap-1">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <Button
                                                key={pageNum}
                                                variant={currentPage === pageNum ? "default" : "outline"}
                                                size="sm"
                                                className="w-8 h-8 p-0"
                                                onClick={() => setCurrentPage(pageNum)}
                                                data-testid={`page-${pageNum}-btn`}
                                            >
                                                {pageNum}
                                            </Button>
                                        );
                                    })}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    data-testid="next-page-btn"
                                >
                                    Siguiente
                                    <ChevronRight size={16} className="ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Alert Dialog para confirmar eliminación */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {servicioToDelete 
                                ? '¿Eliminar este servicio?' 
                                : `¿Eliminar ${selectedIds.length} servicios?`
                            }
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {servicioToDelete 
                                ? `Se eliminará el servicio "${servicioToDelete.descripcion}". Esta acción no se puede deshacer.`
                                : `Se eliminarán ${selectedIds.length} servicios seleccionados. Esta acción no se puede deshacer.`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleConfirmDelete}
                            disabled={deleting}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {deleting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Eliminando...
                                </>
                            ) : (
                                'Eliminar'
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Nuevo Servicio</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="cliente_id">Cliente *</Label>
                                <Select 
                                    value={formData.cliente_id} 
                                    onValueChange={(v) => setFormData({...formData, cliente_id: v})}
                                >
                                    <SelectTrigger data-testid="select-cliente">
                                        <SelectValue placeholder="Seleccionar cliente" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {clientes.map((cliente) => (
                                            <SelectItem key={cliente.id} value={cliente.id}>
                                                {cliente.razon_social}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="nombre_producto">Nombre Producto</Label>
                                <Input
                                    id="nombre_producto"
                                    value={formData.nombre_producto}
                                    onChange={(e) => setFormData({...formData, nombre_producto: e.target.value})}
                                    placeholder="Ej: Licencia Software, Servicio de Consultoría"
                                    data-testid="input-nombre-producto"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="faena">Faena</Label>
                                <Input
                                    id="faena"
                                    value={formData.faena}
                                    onChange={(e) => setFormData({...formData, faena: e.target.value})}
                                    placeholder="Ej: Mina Los Pelambres, Obra Edificio Central"
                                    data-testid="input-faena"
                                />
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="ruta_cobro">Ruta de Cobro <span className="text-xs text-slate-400 font-normal">(opcional)</span></Label>
                                <Select
                                    value={formData.ruta_cobro_id || 'none'}
                                    onValueChange={(v) => setFormData({...formData, ruta_cobro_id: v === 'none' ? '' : v})}
                                >
                                    <SelectTrigger data-testid="select-ruta-cobro">
                                        <SelectValue placeholder="Selecciona una ruta de cobro" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Sin ruta asignada</SelectItem>
                                        {rutas.map((r) => (
                                            <SelectItem key={r.id} value={r.id}>
                                                {r.nombre}{r.es_default ? ' (por defecto)' : ''}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {rutas.length === 0 && (
                                    <p className="text-xs text-slate-400">
                                        No hay rutas configuradas. Crea una en{' '}
                                        <a href="/rutas-cobro" className="text-[#3D7A7A] underline">Rutas de Cobro</a>.
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="tipo_pago">Tipo de Pago *</Label>
                                <Select 
                                    value={formData.tipo_pago} 
                                    onValueChange={(v) => setFormData({...formData, tipo_pago: v})}
                                >
                                    <SelectTrigger data-testid="select-tipo-pago">
                                        <SelectValue placeholder="Seleccionar tipo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="unico">Pago Único</SelectItem>
                                        <SelectItem value="mensual">Pago Mensual</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="condicion_pago_dias">Días de Pago</Label>
                                <Input
                                    id="condicion_pago_dias"
                                    type="number"
                                    value={formData.condicion_pago_dias}
                                    onChange={(e) => setFormData({...formData, condicion_pago_dias: parseInt(e.target.value)})}
                                />
                            </div>
                            
                            {/* Período de Facturación */}
                            <div className="md:col-span-2 p-4 bg-[#E8F4F4] rounded-lg border border-[#A5D0D0]">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-medium text-[#1A3D3D]">Período de Facturación</h4>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.es_recurrente}
                                            onChange={(e) => setFormData({...formData, es_recurrente: e.target.checked})}
                                            className="w-4 h-4 rounded border-[#8BC5C5] text-[#5B9A9A] focus:ring-blue-500"
                                            data-testid="checkbox-recurrente"
                                        />
                                        <span className="text-sm font-medium text-[#2D5A5A]">Servicio recurrente (todos los meses)</span>
                                    </label>
                                </div>
                                
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="periodo_mes">{formData.es_recurrente ? 'Mes Inicio *' : 'Mes *'}</Label>
                                        <Select 
                                            value={String(formData.periodo_mes)} 
                                            onValueChange={(v) => setFormData({...formData, periodo_mes: parseInt(v)})}
                                        >
                                            <SelectTrigger data-testid="select-periodo-mes">
                                                <SelectValue placeholder="Mes" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                                                  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((mes, idx) => (
                                                    <SelectItem key={idx + 1} value={String(idx + 1)}>
                                                        {mes}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="periodo_anio">{formData.es_recurrente ? 'Año Inicio *' : 'Año *'}</Label>
                                        <Select 
                                            value={String(formData.periodo_anio)} 
                                            onValueChange={(v) => setFormData({...formData, periodo_anio: parseInt(v)})}
                                        >
                                            <SelectTrigger data-testid="select-periodo-anio">
                                                <SelectValue placeholder="Año" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map((anio) => (
                                                    <SelectItem key={anio} value={String(anio)}>
                                                        {anio}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    
                                    {formData.es_recurrente && (
                                        <>
                                            <div className="space-y-2">
                                                <Label htmlFor="periodo_fin_mes">Mes Fin *</Label>
                                                <Select 
                                                    value={String(formData.periodo_fin_mes)} 
                                                    onValueChange={(v) => setFormData({...formData, periodo_fin_mes: parseInt(v)})}
                                                >
                                                    <SelectTrigger data-testid="select-periodo-fin-mes">
                                                        <SelectValue placeholder="Mes" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                                                          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((mes, idx) => (
                                                            <SelectItem key={idx + 1} value={String(idx + 1)}>
                                                                {mes}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label htmlFor="periodo_fin_anio">Año Fin *</Label>
                                                <Select 
                                                    value={String(formData.periodo_fin_anio)} 
                                                    onValueChange={(v) => setFormData({...formData, periodo_fin_anio: parseInt(v)})}
                                                >
                                                    <SelectTrigger data-testid="select-periodo-fin-anio">
                                                        <SelectValue placeholder="Año" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map((anio) => (
                                                            <SelectItem key={anio} value={String(anio)}>
                                                                {anio}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </>
                                    )}
                                </div>
                                
                                {formData.es_recurrente && mesesRecurrentes > 0 && (
                                    <div className="mt-3 p-2 bg-[#D5E8E8] rounded-md">
                                        <p className="text-sm text-[#2D5A5A]">
                                            <strong>Se crearán {mesesRecurrentes} servicios</strong> (uno por cada mes desde {
                                                ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                                                 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][formData.periodo_mes - 1]
                                            } {formData.periodo_anio} hasta {
                                                ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                                                 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'][formData.periodo_fin_mes - 1]
                                            } {formData.periodo_fin_anio})
                                        </p>
                                    </div>
                                )}
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="fecha_inicio">Fecha Inicio *</Label>
                                <Input
                                    id="fecha_inicio"
                                    type="date"
                                    value={formData.fecha_inicio}
                                    onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                                    required
                                    data-testid="input-fecha-inicio"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="fecha_termino">Fecha Término</Label>
                                <Input
                                    id="fecha_termino"
                                    type="date"
                                    value={formData.fecha_termino}
                                    onChange={(e) => setFormData({...formData, fecha_termino: e.target.value})}
                                />
                            </div>
                            
                            {/* Sección de Montos con cálculo automático */}
                            <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg border">
                                <div className="flex items-center justify-between mb-3">
                                    <h4 className="font-medium text-slate-700">Valores</h4>
                                    <div className="flex items-center space-x-2">
                                        <Checkbox 
                                            id="exento_iva"
                                            checked={formData.exento_iva}
                                            onCheckedChange={(checked) => setFormData({...formData, exento_iva: checked})}
                                            data-testid="checkbox-exento-iva"
                                        />
                                        <Label 
                                            htmlFor="exento_iva" 
                                            className="text-sm font-normal cursor-pointer text-slate-600"
                                        >
                                            Exento de IVA
                                        </Label>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="monto_neto">Monto Neto (CLP) *</Label>
                                        <Input
                                            id="monto_neto"
                                            type="number"
                                            value={formData.monto_neto}
                                            onChange={(e) => setFormData({...formData, monto_neto: e.target.value})}
                                            required
                                            placeholder="1000000"
                                            data-testid="input-monto"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className={formData.exento_iva ? 'text-slate-400' : ''}>
                                            IVA (19%)
                                        </Label>
                                        <div className={`h-10 px-3 py-2 border rounded-md flex items-center ${
                                            formData.exento_iva 
                                                ? 'bg-slate-50 text-slate-400 line-through' 
                                                : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            {formData.exento_iva ? 'Exento' : (iva > 0 ? `$${iva.toLocaleString('es-CL', { maximumFractionDigits: 0 })}` : '-')}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Monto Total</Label>
                                        <div className="h-10 px-3 py-2 bg-[#E8F4F4] border border-[#A5D0D0] rounded-md text-[#3D7A7A] font-semibold flex items-center">
                                            {montoTotal > 0 ? `$${montoTotal.toLocaleString('es-CL', { maximumFractionDigits: 0 })}` : '-'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Opciones adicionales */}
                            <div className="md:col-span-2 p-4 bg-slate-50 rounded-lg border">
                                <h4 className="font-medium text-slate-700 mb-3">Opciones</h4>
                                <div className="space-y-3">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox 
                                            id="requiere_edp"
                                            checked={formData.requiere_edp}
                                            onCheckedChange={(checked) => setFormData({...formData, requiere_edp: checked})}
                                            data-testid="checkbox-requiere-edp"
                                        />
                                        <div>
                                            <Label 
                                                htmlFor="requiere_edp" 
                                                className="text-sm font-medium cursor-pointer"
                                            >
                                                Requiere EDP (Estado de Pago)
                                            </Label>
                                            <p className="text-xs text-slate-500 mt-0.5">
                                                El servicio requerirá enviar el EDP al cliente cada día 25 para ser autorizado y recibir la OC antes de facturar.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="observaciones">Observaciones</Label>
                                <Textarea
                                    id="observaciones"
                                    value={formData.observaciones}
                                    onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                                    rows={2}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" data-testid="save-servicio-btn">
                                Crear servicio
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Upload Dialog */}
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FileText className="text-[#5B9A9A]" size={24} />
                            Carga Masiva de Servicios
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        <p className="text-sm text-slate-600">
                            Descargue la plantilla Excel, complete los datos de sus servicios y súbala para registrarlos de forma masiva.
                        </p>
                        
                        <div className="p-4 bg-[#E8F4F4] border border-[#A5D0D0] rounded-lg">
                            <h4 className="font-medium text-[#1A3D3D] mb-2">Paso 1: Descargar plantilla</h4>
                            <p className="text-xs text-[#3D7A7A] mb-2">
                                La plantilla incluye la lista de clientes disponibles con sus RUT
                            </p>
                            <Button 
                                variant="outline" 
                                className="w-full"
                                onClick={handleDownloadTemplate}
                                data-testid="download-servicios-template-btn"
                            >
                                <Download size={18} className="mr-2" />
                                Descargar Plantilla Excel
                            </Button>
                        </div>

                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <h4 className="font-medium text-green-900 mb-2">Paso 2: Subir archivo</h4>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileUpload}
                                className="hidden"
                                data-testid="file-input-servicios"
                            />
                            <Button 
                                className="w-full bg-green-600 hover:bg-green-700"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="upload-servicios-file-btn"
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 size={18} className="mr-2 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        <Upload size={18} className="mr-2" />
                                        Seleccionar Archivo Excel
                                    </>
                                )}
                            </Button>
                        </div>

                        <div className="text-xs text-slate-500 space-y-1">
                            <p>• Campos obligatorios: RUT Cliente, Descripción, Fecha Inicio, Monto Neto</p>
                            <p>• El IVA (19%) se calcula automáticamente</p>
                            <p>• Los clientes deben existir previamente</p>
                            <p>• Máximo 500 servicios por carga</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Servicios;
